import { Observable, map } from 'rxjs';
import { Invoice, InvoicesSummary } from '@app/shared/interfaces/invoice.interface';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API, API_ROUTES } from '@app/shared/utils/api-routes';
import { environment } from '@environments/environment';
import { createHttpParams } from '@app/shared/utils/utils';

export interface InvoicesListResponse {
  list: { Items: Invoice[]; Count: number };
  list_error: { Items: Invoice[]; Count: number };
  counters?: { total: number; validated: number };
  filterValues?: { [key: string]: string[] };
}

@Injectable({
  providedIn: 'root',
})
export class InvoicesService {
  constructor(private httpClient: HttpClient) {}

  public getInvoices(params: { [key: string]: string }): Observable<InvoicesListResponse> {
    return this.httpClient
      .get(`${environment.BA_API_BASE_URL}${API.PPA}${API_ROUTES.INVOICE}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        params: createHttpParams(params),
      })
      .pipe(map((res) => res)) as Observable<InvoicesListResponse>;
  }

  public getInvoicesSummary(): Observable<InvoicesSummary> {
    return this.httpClient.get<InvoicesSummary>(
      `${environment.BA_API_BASE_URL}${API.PPA}${API_ROUTES.SUMMARY_INVOICES}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  public downloadInvoice(id: string): Observable<{ data: string }> {
    return this.httpClient
      .get(`${environment.BA_API_BASE_URL}${API.EDITION}${API_ROUTES.INVOICE}/${id}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(map((res) => res)) as Observable<{ data: string }>;
  }

  public putInvoices(invoices: Partial<Invoice>): Observable<{ Items: Invoice }> {
    const { prm: id, ...rest } = invoices;
    return this.httpClient
      .put(
        `${environment.BA_API_BASE_URL}${API.PPA}${API_ROUTES.INVOICE}/${encodeURIComponent(id!)}`,
        { ...rest },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .pipe(map((res) => res)) as Observable<{ Items: Invoice }>;
  }

  public sendInvoices(invoices: Partial<Invoice>[]): Observable<unknown> {
    return this.httpClient
      .post(
        `${environment.BA_API_BASE_URL}${API.PPA}${API_ROUTES.INVOICE}${API_ROUTES.SEND}`,
        { invoices },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .pipe(map((res) => res)) as Observable<unknown>;
  }

  public deleteInvoices(invoices: Partial<Invoice>[]): Observable<
    {
      status: string;
      timestamp: string;
      prm: string;
      id: string;
      error?: string;
    }[]
  > {
    return this.httpClient
      .delete(`${environment.BA_API_BASE_URL}${API.PPA}${API_ROUTES.INVOICE}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        body: { invoices },
      })
      .pipe(map((res) => res)) as Observable<
      {
        status: string;
        timestamp: string;
        prm: string;
        id: string;
        error?: string;
      }[]
    >;
  }
}
